import { observer } from "mobx-react-lite";
import * as React from "react";

export type StopSaving = (status?: "success" | "error") => void;

interface ContextValue {
  content: [
    HTMLElement | null,
    React.Dispatch<React.SetStateAction<HTMLElement | null>>,
  ];
  saving: Saving;
  startSaving: () => StopSaving;
  withSaving: <T>(request: Promise<T>) => Promise<T>;
  hasModalSavingIndicator: boolean;
  registerModalSavingIndicator: () => () => void;
}

interface Saving {
  status: "hidden" | "loading" | "success" | "error";
}

export const AuthenticatedNavigationContext =
  React.createContext<ContextValue | null>(null);

export const AuthenticatedNavigationContextProvider = observer(
  function AuthenticatedNavigationContextProvider(
    props: React.PropsWithChildren,
  ) {
    const content = React.useState<HTMLElement | null>(null);
    const [saving, setSaving] = React.useState<Saving>({
      status: "hidden",
    });
    const [modalSavingIndicatorCount, setModalSavingIndicatorCount] =
      React.useState(0);
    const latestStartCallIdentifier = React.useRef({});
    const hideSaveSuccessTimeoutRef = React.useRef(0);
    const startSuccessTimeoutRef = React.useRef(0);

    const startSaving = React.useCallback(function startSaving() {
      const startTime = Date.now();

      const currentIdentifier = {};
      latestStartCallIdentifier.current = currentIdentifier;

      clearTimeout(hideSaveSuccessTimeoutRef.current);
      clearTimeout(startSuccessTimeoutRef.current);

      setSaving({
        status: "loading",
      });

      return (status: "success" | "error" = "success") => {
        if (latestStartCallIdentifier.current !== currentIdentifier) {
          return;
        }

        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, 300 - elapsed);

        startSuccessTimeoutRef.current = window.setTimeout(() => {
          if (latestStartCallIdentifier.current !== currentIdentifier) {
            return;
          }

          setSaving({
            status,
          });
        }, delay);

        hideSaveSuccessTimeoutRef.current = window.setTimeout(() => {
          if (latestStartCallIdentifier.current !== currentIdentifier) {
            return;
          }

          setSaving({
            status: "hidden",
          });
        }, delay + 2700);
      };
    }, []);

    const registerModalSavingIndicator = React.useCallback(
      function registerModalSavingIndicator() {
        setModalSavingIndicatorCount((count) => {
          return count + 1;
        });

        return () => {
          setModalSavingIndicatorCount((count) => {
            return count - 1;
          });
        };
      },
      [],
    );

    const value = React.useMemo(() => {
      async function withSaving<T>(request: Promise<T>) {
        const stopSaving = startSaving();

        try {
          const response = await request;
          stopSaving();
          return response;
        } catch (error) {
          stopSaving("error");
          throw error;
        }
      }

      return {
        content,
        saving,
        startSaving,
        withSaving,
        hasModalSavingIndicator: modalSavingIndicatorCount > 0,
        registerModalSavingIndicator,
      };
    }, [
      content,
      saving,
      startSaving,
      modalSavingIndicatorCount,
      registerModalSavingIndicator,
    ]);

    return (
      <AuthenticatedNavigationContext.Provider value={value}>
        {props.children}
      </AuthenticatedNavigationContext.Provider>
    );
  },
);

export function useAuthenticatedNavigationContext() {
  const context = React.useContext(AuthenticatedNavigationContext);

  if (!context) {
    throw new Error(
      "useAuthenticatedNavigationContext must be used within a AuthenticatedNavigationContextProvider",
    );
  }

  return context;
}

export function useSetContentElement() {
  const { content } = useAuthenticatedNavigationContext();

  return content[1];
}

export function useContentElement() {
  const { content } = useAuthenticatedNavigationContext();

  return content[0];
}
